import type { QuizSchema, QuizQuestion } from "@/types/quiz";

type Answers = Record<string, number | number[]>;

function norm0to100(v0to4: number) {
  const v = Math.max(0, Math.min(4, v0to4));
  return Math.round((v / 4) * 100);
}

function weightedAvg(values: number[], weights: number[]) {
  const wsum = weights.reduce((a, b) => a + b, 0) || 1;
  const vsum = values.reduce((s, v, i) => s + v * (weights[i] ?? 1), 0);
  return vsum / wsum;
}

/** Compute Tolerance/Capacity/Preference and Final. */
export function computeScores(answers: Answers, quiz: QuizSchema) {
  const dims: Record<"tolerance" | "capacity" | "preference", { vals: number[]; wts: number[] }> = {
    tolerance: { vals: [], wts: [] },
    capacity: { vals: [], wts: [] },
    preference: { vals: [], wts: [] }
  };

  const qIndex: Record<string, QuizQuestion> = Object.fromEntries(
    quiz.questions.map(q => [q.id, q])
  );

  for (const [qid, raw] of Object.entries(answers)) {
    const q = qIndex[qid];
    if (!q) continue;
    const values = Array.isArray(raw) ? (raw as number[]) : [raw as number];
    // For multi-select, use MAX selection (most permissive signal)
    const v0to4 = Math.max(...values.map(v => Number(v) || 0));

    // Spec v2.1: q5 contributes 0.5 to tolerance and 0.5 to preference
    if (q.id === "q5_growth_vs_stability") {
      dims.tolerance.vals.push(v0to4);
      dims.tolerance.wts.push(0.5);
      dims.preference.vals.push(v0to4);
      dims.preference.wts.push(0.5);
      continue; // do not also add to its declared dimension
    }

    const dim = (q.dimension ?? "tolerance") as "tolerance" | "capacity" | "preference";
    const wt = q.weight ?? 1;
    dims[dim].vals.push(v0to4);
    dims[dim].wts.push(wt);
  }

  const tol0to4 = weightedAvg(dims.tolerance.vals, dims.tolerance.wts) || 0;
  const cap0to4 = weightedAvg(dims.capacity.vals, dims.capacity.wts) || 0;
  const pref0to4 = weightedAvg(dims.preference.vals, dims.preference.wts) || 0;

  const tolerance = norm0to100(tol0to4);
  const capacity  = norm0to100(cap0to4);
  const preference = norm0to100(pref0to4);

  // Spec v2.1: preference nudge ±10
  const nudge = Math.round((preference - 50) * 0.20);

  // Composite & guardrails per spec
  let finalScore: number;
  if (capacity < 40) {
    finalScore = Math.max(0, Math.min(100, capacity + nudge));
  } else {
    const blended = 0.7 * capacity + 0.3 * tolerance;
    finalScore = Math.max(0, Math.min(100, Math.round(blended + nudge)));
  }

  // Flags for messaging
  const flags = {
    capacity_constrained: tolerance - capacity >= 15, // wants risk > can afford
    risk_seek_high: tolerance >= 75,
    risk_averse: tolerance <= 25,
  };

  return { tolerance, capacity, preference, finalScore, flags };
}
